import { useState, useRef, useCallback } from "react";
import { Camera, Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

export interface VisualMatch {
  productHandle: string;
  productTitle: string;
  productImage: string;
  productPrice: string;
  similarityScore: number;
  matchReason: string;
}

interface ImageSearchUploaderProps {
  onAnalyzeComplete: (searchTerms: string[], description: string, visualMatches?: VisualMatch[]) => void;
  onCancel: () => void;
  isAnalyzing: boolean;
  setIsAnalyzing: (analyzing: boolean) => void;
}

interface AnalyzeResult {
  searchTerms: string[];
  description: string;
  category: string;
  confidence: number;
  visualMatches?: VisualMatch[];
  totalCompared?: number;
  error?: string;
}

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/jpg", "image/heic", "image/heif"];

export const ImageSearchUploader = ({
  onAnalyzeComplete,
  onCancel,
  isAnalyzing,
  setIsAnalyzing,
}: ImageSearchUploaderProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [analysisStatus, setAnalysisStatus] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Aggressive image compression - handles any size
  const compressImage = useCallback(async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          
          // Calculate new dimensions - max 800px for faster processing
          let width = img.width;
          let height = img.height;
          const maxDim = 800;
          
          if (width > height && width > maxDim) {
            height = (height * maxDim) / width;
            width = maxDim;
          } else if (height > maxDim) {
            width = (width * maxDim) / height;
            height = maxDim;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          ctx?.drawImage(img, 0, 0, width, height);
          
          // Start with quality 0.7, reduce if still too large
          let quality = 0.7;
          let base64 = canvas.toDataURL("image/jpeg", quality);
          
          // If still too large, reduce quality further
          while (base64.length > 500000 && quality > 0.2) {
            quality -= 0.1;
            base64 = canvas.toDataURL("image/jpeg", quality);
          }
          
          // If still too large, reduce dimensions
          if (base64.length > 500000) {
            const smallerMaxDim = 600;
            let newWidth = width;
            let newHeight = height;
            
            if (newWidth > newHeight && newWidth > smallerMaxDim) {
              newHeight = (newHeight * smallerMaxDim) / newWidth;
              newWidth = smallerMaxDim;
            } else if (newHeight > smallerMaxDim) {
              newWidth = (newWidth * smallerMaxDim) / newHeight;
              newHeight = smallerMaxDim;
            }
            
            canvas.width = newWidth;
            canvas.height = newHeight;
            ctx?.drawImage(img, 0, 0, newWidth, newHeight);
            base64 = canvas.toDataURL("image/jpeg", 0.6);
          }
          
          resolve(base64);
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const analyzeImage = useCallback(async (base64Image: string) => {
    setIsAnalyzing(true);
    setProgress(5);
    setAnalysisStatus("מכין תמונה...");

    try {
      // Progress steps for visual search (takes longer)
      const progressSteps = [
        { value: 10, text: "מזהה את סוג התכשיט..." },
        { value: 25, text: "מחפש מוצרים מתאימים..." },
        { value: 45, text: "טוען תמונות מוצרים..." },
        { value: 60, text: "משווה תמונות..." },
        { value: 75, text: "מדרג לפי דמיון..." },
        { value: 85, text: "מסיים עיבוד..." },
      ];

      let stepIndex = 0;
      const progressInterval = setInterval(() => {
        if (stepIndex < progressSteps.length) {
          setProgress(progressSteps[stepIndex].value);
          setAnalysisStatus(progressSteps[stepIndex].text);
          stepIndex++;
        }
      }, 5000); // Every 5 seconds

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-jewelry-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ 
            image: base64Image,
            mode: "visual"
          }),
        }
      );

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          throw new Error("המערכת עמוסה. נסה שוב בעוד מספר דקות.");
        }
        if (response.status === 402) {
          throw new Error("יש בעיה בשירות. נסה שוב מאוחר יותר.");
        }
        throw new Error(errorData.error || "שגיאה בניתוח התמונה");
      }

      setProgress(95);
      setAnalysisStatus("מסיים...");

      const result: AnalyzeResult = await response.json();

      if (result.error && result.confidence === 0) {
        throw new Error(result.error);
      }

      setProgress(100);
      
      const matchCount = result.visualMatches?.length || 0;
      if (matchCount > 0) {
        setAnalysisStatus(`נמצאו ${matchCount} מוצרים דומים!`);
      } else {
        setAnalysisStatus(`זוהה: ${result.category}`);
      }

      // Wait a moment to show the result
      await new Promise((resolve) => setTimeout(resolve, 500));

      onAnalyzeComplete(result.searchTerms, result.description, result.visualMatches);
    } catch (error) {
      console.error("Error analyzing image:", error);
      toast({
        title: "שגיאה",
        description: error instanceof Error ? error.message : "שגיאה בניתוח התמונה",
        variant: "destructive",
      });
      setIsAnalyzing(false);
      setProgress(0);
      setAnalysisStatus("");
    }
  }, [onAnalyzeComplete, setIsAnalyzing]);

  const handleFile = useCallback(async (file: File) => {
    // Check if it's an image file
    const isImage = file.type.startsWith("image/") || 
                    ACCEPTED_TYPES.some(t => file.type.includes(t.split("/")[1]));
    
    if (!isImage) {
      toast({
        title: "קובץ לא נתמך",
        description: "יש להעלות קובץ תמונה",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsAnalyzing(true);
      setProgress(2);
      setAnalysisStatus("מעבד תמונה...");
      
      const compressed = await compressImage(file);
      setPreviewUrl(compressed);
      await analyzeImage(compressed);
    } catch (error) {
      console.error("Error processing file:", error);
      toast({
        title: "שגיאה",
        description: "שגיאה בעיבוד התמונה, נסה תמונה אחרת",
        variant: "destructive",
      });
      setIsAnalyzing(false);
      setProgress(0);
      setAnalysisStatus("");
    }
  }, [compressImage, analyzeImage, setIsAnalyzing]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleClear = useCallback(() => {
    setPreviewUrl(null);
    setProgress(0);
    setAnalysisStatus("");
    setIsAnalyzing(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  }, [setIsAnalyzing]);

  return (
    <div className="space-y-4">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Preview and Analysis State */}
      {previewUrl ? (
        <div className="relative space-y-3">
          <div className="relative aspect-square max-w-48 mx-auto rounded-lg overflow-hidden border border-border">
            <img
              src={previewUrl}
              alt="תמונה לחיפוש"
              className="w-full h-full object-cover"
            />
            {!isAnalyzing && (
              <Button
                variant="secondary"
                size="icon"
                className="absolute top-2 right-2 h-7 w-7 bg-background/80 hover:bg-background"
                onClick={handleClear}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            {isAnalyzing && (
              <div className="absolute inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
          </div>

          {isAnalyzing && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-sm text-center text-muted-foreground">
                {analysisStatus}
              </p>
              <p className="text-xs text-center text-muted-foreground/70">
                החיפוש הויזואלי עשוי לקחת עד דקה
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={cn(
              "border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition-all cursor-pointer",
              isDragging
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 hover:bg-muted/30"
            )}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ImageIcon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm sm:text-base">גרור תמונה לכאן</p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  או לחץ לבחירת קובץ
                </p>
              </div>
              <p className="text-xs text-primary/70 mt-1">
                חיפוש ויזואלי מתקדם - מוצא מוצרים דומים לפי תמונה
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 sm:flex-none"
            >
              <Upload className="h-4 w-4 ml-2" />
              העלה תמונה
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => cameraInputRef.current?.click()}
              className="flex-1 sm:flex-none"
            >
              <Camera className="h-4 w-4 ml-2" />
              צלם תמונה
            </Button>
          </div>
        </>
      )}

      {/* Cancel button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="w-full"
        disabled={isAnalyzing}
      >
        ביטול
      </Button>
    </div>
  );
};
